import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Layout, Button, List, Checkbox, Modal, Avatar } from "antd";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { Locale, ApiManager } from "../../utils";
import Base from "../Base";
import { TIANDITU_ZOOM } from "../../constants";
import {
  createSquarePolygonPoints,
  loadTianditu,
  searchPoisInBounds,
} from "../../utils/tianditu";

const { Content, Header } = Layout;

const i18n = Locale.getInstance();
const base = Base.getInstance();
const api = ApiManager.getInstance();

function mergePoiList(existingList, newItems) {
  const merged = [...existingList];
  newItems.forEach((item) => {
    if (merged.findIndex((entry) => entry.name === item.name) === -1) {
      merged.push(item);
    }
  });
  return merged;
}

function ServiceAreaSetting({ visible, onClose }) {
  const mapInstance = useRef(null);
  const polygonRef = useRef(null);
  const polygonToolRef = useRef(null);
  const drawHandlerRef = useRef(null);
  const user = useSelector((state) => getUserInfo(state));
  const [selectedList, setSelectedList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  const runPolygonSearch = useCallback(async (map, polygon) => {
    if (!polygon || typeof polygon.getBounds !== "function") {
      return;
    }

    setSearching(true);
    try {
      const pois = await searchPoisInBounds(map, polygon.getBounds());
      setSelectedList((list) => mergePoiList(list, pois));
    } catch (error) {
      console.log("ServiceAreaSetting search error", error);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!visible || !user.merchant?.longitude || !user.merchant?.latitude) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const T = await loadTianditu();
        if (cancelled) {
          return;
        }

        const { longitude, latitude } = user.merchant;
        const map = new T.Map("map-container");
        map.centerAndZoom(
          new T.LngLat(parseFloat(longitude), parseFloat(latitude)),
          TIANDITU_ZOOM
        );

        const shopMarker = new T.Marker(
          new T.LngLat(parseFloat(longitude), parseFloat(latitude))
        );
        map.addOverLay(shopMarker);

        const polygon = new T.Polygon(
          createSquarePolygonPoints(T, longitude, latitude),
          {
            color: "#1677ff",
            weight: 2,
            opacity: 0.8,
            fillColor: "#1677ff",
            fillOpacity: 0.2,
          }
        );
        map.addOverLay(polygon);
        polygonRef.current = polygon;

        const polygonTool = new T.PolygonTool(map, {
          showLabel: false,
          color: "#1677ff",
          weight: 2,
          opacity: 0.8,
          fillColor: "#1677ff",
          fillOpacity: 0.2,
        });
        polygonToolRef.current = polygonTool;

        const handleDraw = async (event) => {
          const points = event.currentLnglats || event.currentLngLats;
          if (!points || points.length < 3) {
            return;
          }

          if (polygonRef.current) {
            map.removeOverLay(polygonRef.current);
          }

          polygonRef.current = new T.Polygon(points, {
            color: "#1677ff",
            weight: 2,
            opacity: 0.8,
            fillColor: "#1677ff",
            fillOpacity: 0.2,
          });
          map.addOverLay(polygonRef.current);
          await runPolygonSearch(map, polygonRef.current);
        };

        drawHandlerRef.current = handleDraw;
        polygonTool.addEventListener("draw", handleDraw);

        mapInstance.current = {
          map,
          resize() {
            if (typeof map.checkResize === "function") {
              map.checkResize();
            }
          },
          destroy() {
            if (drawHandlerRef.current && polygonToolRef.current) {
              polygonToolRef.current.removeEventListener(
                "draw",
                drawHandlerRef.current
              );
            }
            polygonToolRef.current?.close?.();
            map.clearOverLays();
          },
        };

        mapInstance.current.resize();
        await runPolygonSearch(map, polygon);
      } catch (error) {
        console.log("ServiceAreaSetting map init error", error);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      mapInstance.current?.destroy?.();
      mapInstance.current = null;
      polygonRef.current = null;
      polygonToolRef.current = null;
      drawHandlerRef.current = null;
    };
  }, [visible, user.merchant, runPolygonSearch]);

  useEffect(() => {
    if (!visible) {
      setSelectedList([]);
    }
  }, [visible]);

  const handleStartDrawing = useCallback(() => {
    polygonToolRef.current?.open?.();
  }, []);

  const handleRefreshSearch = useCallback(() => {
    if (mapInstance.current?.map && polygonRef.current) {
      runPolygonSearch(mapInstance.current.map, polygonRef.current);
    }
  }, [runPolygonSearch]);

  const onFinish = useCallback(async () => {
    const { token } = user;
    if (token) {
      const list = selectedList.filter((i) => i.checked);
      if (list.length > 0) {
        let apiUrl = api.community.add;
        try {
          setLoading(true);
          const res = await Promise.all(
            list.map((item) => {
              const { location, name, address } = item;
              let apiConfig = {
                token,
                data: {
                  zh_name: name,
                  ug_name: name,
                  zh_address: address,
                  ug_address: address,
                  order: 0,
                  latitude: location.lat,
                  longitude: location.lng,
                  population: 0,
                },
              };
              return apiUrl.run(apiConfig);
            })
          );
          if (Array.isArray(res) && res.every((i) => i.result)) {
            base.showMessage(i18n.t("community.success"), Base.Message.success);
            onClose();
          } else {
            base.showMessage(i18n.t("base.api.errUnknown"), Base.Message.error);
          }
        } catch (error) {
          base.handleApiError(apiUrl, error);
        } finally {
          setLoading(false);
        }
      } else {
        base.showMessage("至少选择一个小区", Base.Message.warning);
      }
    }
  }, [selectedList, user, onClose]);

  return (
    <Modal
      forceRender
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={
        <Button loading={loading} onClick={onFinish} type="primary">
          {i18n.t("community.add")}
        </Button>
      }
      afterOpenChange={(open) => {
        if (open) {
          setTimeout(() => mapInstance.current?.resize?.(), 100);
        }
      }}
    >
      <Layout>
        <Header style={{ backgroundColor: "#fff", padding: "8px 0" }}>
          <Button onClick={handleStartDrawing} style={{ marginRight: 8 }}>
            绘制配送范围
          </Button>
          <Button loading={searching} onClick={handleRefreshSearch}>
            重新搜索范围内小区
          </Button>
        </Header>
        <Content
          style={{
            display: "flex",
            flexDirection: "row",
            position: "relative",
          }}
        >
          <div id="map-container" style={{ height: 500, width: "100%" }} />
        </Content>
        <List
          bordered
          loading={loading || searching}
          style={{
            backgroundColor: "#fff",
            maxHeight: 400,
            overflowY: "scroll",
          }}
          dataSource={selectedList}
          renderItem={(item) => (
            <List.Item key={item.name}>
              <Checkbox
                checked={item.checked}
                onChange={() => {
                  let list = selectedList;
                  const idx = list.findIndex((i) => i.name === item.name);
                  list.splice(idx, 1, { ...item, checked: !item.checked });
                  setSelectedList([...list]);
                }}
                style={{ marginLeft: 20, marginRight: 20 }}
              />
              <List.Item.Meta
                avatar={
                  <Avatar
                    size="large"
                    shape="square"
                    src={Array.isArray(item.photos) ? item.photos[0]?.url : ""}
                  />
                }
                title={item.name}
                description={`${item.cityname} ${item.adname} ${item.address}`}
              />
            </List.Item>
          )}
        />
      </Layout>
    </Modal>
  );
}

export default ServiceAreaSetting;
